import { Button } from "@/shadcn/components/ui/button";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import Placeholder from "@tiptap/extension-placeholder"; // Import the Placeholder extension
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { common, createLowlight } from "lowlight";
import { Send } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TurndownService from "turndown";
const turndownService = new TurndownService();

// Create a lowlight instance with common languages
const lowlight = createLowlight(common);

type InputAreaProps = {
  onSendMessage: (markdownContent: string) => void; // Accept Markdown content instead of HTML
  isSending: boolean;
  waitingForResponse: boolean;
};

const InputArea: React.FC<InputAreaProps> = ({
  onSendMessage,
  isSending,
  waitingForResponse,
}) => {
  const { t } = useTranslation();
  const [editorContent, setEditorContent] = useState("");
  const editor = useEditor({
    editable: !isSending,
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Placeholder.configure({
        placeholder: t("Type your message here..."),
      }),
    ],
    content: undefined,
    editorProps: {
      attributes: {
        class: "prose prose-invert focus:outline-none min-h-[100px]",
      },
    },
    onUpdate: ({ editor }) => {
      setEditorContent(editor.getText());
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  useEffect(() => {
    if (!editor) {
      console.error("Editor is not initialized");
    }
  }, [editor]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isSending); // Dynamically update the editor's editable state based on isSending
    }
  }, [editor, isSending]); // Re-run whenever editor or isSending changes

  const handleSendMessage = () => {
    if (isSending) {
      return; // Prevent sending if already sending
    }

    if (waitingForResponse) {
      return; // Prevent sending if waiting for a response
    }

    if (!editor) {
      console.error("Editor is not initialized");
      return;
    }

    if (editorContent.trim().length === 0) {
      return;
    }

    const content = editor.getHTML(); // Get the HTML content from the editor
    const markdownContent = turndownService.turndown(content);
    console.log("Markdown content to send:", markdownContent);
    // if (markdownContent) {
    //   return;
    // }

    onSendMessage(markdownContent);

    // Clear the editor content after sending the message
    editor.commands.clearContent();
  };

  return (
    <div className="mt-0 flex-shrink-0 rounded-md border border-gray-800 bg-gray-950 p-2">
      <EditorContent
        editor={editor}
        className="max-h-[40vh] min-h-[50px] overflow-y-auto focus:outline-none"
        onKeyDown={handleKeyDown}
      />
      <div className="mt-2 flex justify-end">
        <Button
          onClick={handleSendMessage}
          disabled={
            !editor ||
            editorContent.trim().length === 0 ||
            isSending ||
            waitingForResponse
          }
          className="flex items-center gap-2"
        >
          <span>Send</span>
          <Send size={16} />
        </Button>
      </div>
    </div>
  );
};

export default InputArea;
